import { v4 as uuidV4 } from 'uuid';
import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { parseZonedDateTime } from '@internationalized/date';
import { api } from '@/api';
import { getRouteLegsDivided } from '@/queries/route-planner/utils';
import {
  GetLogisticsListParams,
  AircraftUtilization,
  AircraftUtilizationParams,
  LogisticsProjectionType,
  RecommendedFlightsParams,
  ResponseRouteLegTypeEnum,
  RouteProjectionType,
  RouteTypeEnum,
  TmdxDriveAffiliateGroundLegResponse,
  TmdxDriveTedsGroundLegResponse,
  LogisticsResourceType,
} from '@/api/apis/route';
import { getTzDateTimeString } from '@/utils/helpers';
import { queryClient } from '@/queries';
import { routePlannerQueryKeys } from '@/queries/route-planner/route-planner.keys';
import { seedLogisticsRoute } from '@/utils/helpers/queryCache.helpers';
import { driveServiceQuery } from '@/queries/drive-service';
import { caseQueries } from '@/queries/case';
import { MINUTE_IN_MS } from '@/constants/date.constants';
import { CaseResourceType, CaseStatus } from '@/api/apis/case';
import { resourceAvailabilityQueries } from '@/queries/resource/availability';

export const getBenchmarkById = async (benchmarkId: string) => {
  const data = await api.route.getBenchmarkById(benchmarkId);

  return {
    ...data,
    plan: JSON.parse(data.plan),
  };
};

const fetchGroundTrips = async (groundTripIds: string[]) => {
  if (groundTripIds.length) {
    await queryClient.prefetchQuery(driveServiceQuery.list(groundTripIds));
  }
};

const fetchAircraftResourceData = async (resources: LogisticsResourceType[]): Promise<LogisticsResourceType[]> => {
  const hasAircrafts = resources.some((r) => r.resourceType === CaseResourceType.Aircraft);

  if (!hasAircrafts) return resources;

  return Promise.all(
    resources.map(async (r) => {
      if (r.resourceType !== CaseResourceType.Aircraft) return r;

      const list = await queryClient.ensureQueryData(
        resourceAvailabilityQueries.aircraftById({ aircraftIds: r.resourceId }),
      );
      const data = list[0] || undefined;

      return {
        ...r,
        data,
      };
    }),
  );
};

export const fetchLogisticsById = async (logisticsId: string): Promise<LogisticsProjectionType> => {
  const data = await api.route.getLogisticsById(logisticsId);

  seedLogisticsRoute(data);

  const groundTripIds = data.routes
    .flatMap((route) => route.legs)
    .filter((leg): leg is TmdxDriveTedsGroundLegResponse | TmdxDriveAffiliateGroundLegResponse =>
      [ResponseRouteLegTypeEnum.TmdxDriveAffiliateGroundLeg, ResponseRouteLegTypeEnum.TmdxDriveTedsGroundLeg].includes(
        leg.$type,
      ),
    )
    .map((leg) => leg.tripReference?.tripId)
    .filter((tripId): tripId is string => Boolean(tripId));

  await fetchGroundTrips(groundTripIds);
  const resources = await fetchAircraftResourceData(data.resources);

  return {
    ...data,
    resources,
    groundTripIds,
  };
};

const getRouteByType = async (logisticsId: string, routeType: RouteTypeEnum): Promise<RouteProjectionType> => {
  const cachedData = queryClient.getQueryData<RouteProjectionType>(
    routePlannerQueryKeys.getRouteByType(logisticsId, routeType),
  );

  if (!cachedData) {
    const data = await queryClient.ensureQueryData<LogisticsProjectionType>({
      queryKey: routePlannerQueryKeys.getLogistics(logisticsId),
    });

    const route = data.routes.find((r) => r.routeType === routeType)!;

    return {
      ...route,
      ...getRouteLegsDivided(route.legs, data.locations),
    } as RouteProjectionType;
  }

  return cachedData;
};

const AIRCRAFT_UTILIZATION_PAGE_SIZE = 200;

const fetchAircraftUtilization = async (params: AircraftUtilizationParams): Promise<AircraftUtilization[]> => {
  const response = await api.route.getAircraftUtilization({
    ...params,
    pageIndex: 0,
    itemsPerPage: AIRCRAFT_UTILIZATION_PAGE_SIZE,
  });

  if (!response.items.length) return [];

  const caseIds = Array.from(new Set(response.items.map((u) => u.caseId)));
  const caseList = await queryClient.fetchQuery(
    caseQueries.caseListByIds({ caseIds: caseIds.join(','), itemsPerPage: caseIds.length }),
  );

  return response.items.reduce((acc, u) => {
    const caseItem = caseList.items.find((c) => c.caseId === u.caseId);

    if (!caseItem || [CaseStatus.Finished, CaseStatus.Closed].includes(caseItem.status)) return acc;

    return [
      ...acc,
      {
        ...u,
        caseName: caseItem.caseName,
      },
    ];
  }, [] as AircraftUtilization[]);
};

export const routePlannerQueries = {
  getLogisticsList: (params: Partial<GetLogisticsListParams>, enabled: boolean) =>
    queryOptions({
      queryKey: routePlannerQueryKeys.getList(params),
      queryFn: enabled ? () => api.route.getLogisticsList(params) : skipToken,
      enabled,
    }),
  getAircraftUtilization: (params: AircraftUtilizationParams) =>
    queryOptions({
      queryKey: routePlannerQueryKeys.getAircraftUtilization(params),
      queryFn: () => fetchAircraftUtilization(params),
      staleTime: MINUTE_IN_MS * 10,
    }),
  getLogisticsById: (logisticsId?: string) =>
    queryOptions({
      queryKey: routePlannerQueryKeys.getLogistics(logisticsId),
      queryFn: logisticsId ? () => fetchLogisticsById(logisticsId) : skipToken,
      staleTime: Infinity,
      enabled: !!logisticsId,
    }),
  getLogisticsSnapshotByVersion: (logisticsId?: string, version?: number) =>
    queryOptions({
      queryKey: routePlannerQueryKeys.getLogistics(logisticsId, version),
      queryFn: logisticsId && version ? () => api.route.getLogisticsSnapshotByVersion(logisticsId, version) : skipToken,
      staleTime: Infinity,
      enabled: !!logisticsId && !!version,
    }),
  getRouteByType: (logisticsId: string, routeType: RouteTypeEnum) =>
    queryOptions<RouteProjectionType>({
      queryKey: routePlannerQueryKeys.getRouteByType(logisticsId, routeType),
      queryFn: () => getRouteByType(logisticsId, routeType),
      staleTime: Infinity,
      enabled: !!logisticsId,
    }),
  getRecommendedFlights: (params: RecommendedFlightsParams) =>
    queryOptions({
      queryKey: routePlannerQueryKeys.getRecommendedFlights(params),
      queryFn: async () => {
        const data = await api.route.getRecommendedFlights(params);

        return {
          ...data,
          flights: data.flights.map((f) => ({
            ...f,
            id: uuidV4(),
            legs: f.legs.sort((a, b) => {
              const aDateTimeString = getTzDateTimeString(a.departureTime);
              const aZonedDateTimeString = parseZonedDateTime(aDateTimeString).toAbsoluteString();
              const bDateTimeString = getTzDateTimeString(b.departureTime);
              const bZonedDateTimeString = parseZonedDateTime(bDateTimeString).toAbsoluteString();

              return Number(new Date(aZonedDateTimeString)) - Number(new Date(bZonedDateTimeString));
            }),
          })),
        };
      },
    }),
  getBenchmarkById: (benchmarkId?: string) =>
    queryOptions({
      queryKey: routePlannerQueryKeys.getBenchmark(benchmarkId),
      queryFn: () => (benchmarkId ? getBenchmarkById(benchmarkId) : null),
    }),
  getLogisticsLogs: (logisticsId?: string) =>
    queryOptions({
      queryKey: routePlannerQueryKeys.getLogisticsLogs(logisticsId),
      queryFn: logisticsId ? () => api.route.getLogisticsLogs(logisticsId) : skipToken,
      staleTime: Infinity,
      enabled: !!logisticsId,
    }),
};

const useRecommendedFlights = (params: RecommendedFlightsParams) => {
  return useQuery(routePlannerQueries.getRecommendedFlights(params));
};

const useBenchmarkById = (benchmarkId?: string) => {
  return useQuery(routePlannerQueries.getBenchmarkById(benchmarkId));
};

// Route Service V2 Queries
const useLogisticsList = (params: Partial<GetLogisticsListParams>) => {
  const hasParams = !!Object.keys(params).length;
  return useQuery(routePlannerQueries.getLogisticsList(params, hasParams));
};

const useLogisticsById = (logisticsId?: string) => {
  return useQuery(routePlannerQueries.getLogisticsById(logisticsId));
};

const useLogisticsSnapshot = (logisticsId?: string, version?: number) => {
  return useQuery(routePlannerQueries.getLogisticsSnapshotByVersion(logisticsId, version));
};

const useRouteByType = (routeType: RouteTypeEnum, logisticsId: string = '') => {
  return useQuery(routePlannerQueries.getRouteByType(logisticsId, routeType));
};

const useLogisticsLogs = (logisticsId?: string) => {
  return useQuery(routePlannerQueries.getLogisticsLogs(logisticsId));
};

const useAircraftUtilizationQuery = (params: AircraftUtilizationParams, enabled: boolean) => {
  return useQuery({
    ...routePlannerQueries.getAircraftUtilization(params),
    enabled,
  });
};

export {
  useRecommendedFlights,
  useBenchmarkById,
  useAircraftUtilizationQuery,
  // Route Service V2 Queries
  useLogisticsList,
  useLogisticsById,
  useLogisticsSnapshot,
  useRouteByType,
  useLogisticsLogs,
};
